import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import config from 'constants/config'
import { CRUISE_CHECKOUT_INFLOW } from 'constants/checkout'
import { useAppDispatch } from 'hooks/reduxHooks'
import { generateCruiseCheckoutItem } from 'checkout/lib/utils/cruises/cart'
import { addToCheckoutCart, removeItem } from 'actions/CheckoutActions'
import { ShareModalConfig } from 'lib/customer/getShareModalConfig'
import { setInFlowCheckoutStep } from 'actions/CruiseActions'
import { CruiseContentSections, CRUISE_CONTENT_SECTION_LABELS, CruiseSectionTabValues } from 'constants/cruise'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import CruiseFAQ from './FAQ/CruiseFAQ'
import Pane from 'components/Common/Pane'
import CruiseHeadData from './CruiseHeadData'
import CruiseOverview from './CruiseOverview'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import { offerHasValidDepartures } from 'lib/cruises/cruiseUtils'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { pluralizeToString } from 'lib/string/pluralize'
import { fireInteractionEvent } from 'api/googleTagManager'
import { cruiseSelectDepartureClick } from 'analytics/eventDefinitions'
import CruiseVerticalUSPs from './BookWithConfidence/CruiseVerticalUSPs'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import CruiseStickyFooter from './StickyElements/CruiseStickyFooter'
import PaletteProvider from 'contexts/PaletteProvider'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import useQueryParams from 'hooks/useQueryParams'
import { min } from 'lib/array/arrayUtils'
import { replace } from 'connected-react-router'
import CruiseStickyHeader from './StickyElements/CruiseStickyHeader'
import { getCruiseItems } from 'checkout/selectors/view/cruise'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import { getApplicableVelocityFeatures } from 'lib/partnership/partnershipUtils'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import CruiseRecommendations from 'components/Common/OfferRecommendations/CruiseRecommendations'
import CruiseSoldoutCard from 'components/OfferPage/Cruises/CruiseSoldoutCard'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import Label from 'components/Luxkit/Label/Label'
import Group from 'components/utils/Group'
import { fetchPlaceByName } from 'actions/PlaceActions'

import CruiseOfferPageDepartures from './Departures/CruiseOfferPageDepartures'
import CruiseOfferPageItinerary from './Itinerary/CruiseOfferPageItinerary'
import CruiseOfferPageAboutShip from './AboutTheShip/CruiseOfferPageAboutShip'

const USPPane = styled(Pane)`
  padding: ${rem(40)} 0;
`
const GalleryContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)} ${rem(16)} 0;
  }
`

const SectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(32)};
  padding-top: ${rem(32)};

  > .section-title + .section-subtitle {
    margin-top: ${rem(8)};
  }

  > .section-title + .section-content,
  > .section-subtitle + .section-content {
    margin-top: ${rem(24)};
  }

  ${mediaQueryUp.tablet} {
    padding-bottom: ${rem(64)};
    padding-top: ${rem(64)};

    > .section-title + .section-content,
    > .section-subtitle + .section-content {
      margin-top: ${rem(40)};
    }
  }
`

const OverviewSectionContainer = styled(LayoutContainer)`
  padding-top: ${rem(24)};
  padding-bottom: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-bottom: ${rem(64)};
    padding-top: ${rem(24)};
  }
`

const SectionTabButtonsGroup = styled(TabButtonsGroup)`
  background: ${props => props.theme.palette.neutral.eight};
`

type SectionTabs = {
  label: string;
  value: CruiseSectionTabValues;
}

interface Props extends RouteComponentProps {
  cruiseItem: App.Checkout.CruiseItem;
  isSpoofed: boolean;
  departureId: string;
  previousRoute: string
  offer: App.CruiseOffer;
  travellers?: App.CruiseTraveler;
  shareModalConfig?: ShareModalConfig;
  isVelocityEnabled: boolean;
  currentRegionCode: string;
  place:{
    [key: string]: App.Place;
  };
}

function generateFilters(
  offerType: App.OfferType,
  departurePort: string,
  place:{
    [key: string]: App.Place;
  },
  cruiseLine: App.CruiseLine,
) {
  const filters = {} as any
  if (cruiseLine) {
    filters.cruiseLines = [cruiseLine.code]
  }
  if (departurePort) {
    filters.departurePlaceId = place[departurePort]?.id
  }
  if (offerType === OFFER_TYPE_CRUISE) {
    filters.offerTypes = [OFFER_TYPE_CRUISE]
  }
  return filters
}

function determineCruiseContentSections(
  offer: App.CruiseOffer,
): Set<CruiseContentSections> {
  const sections = new Set<CruiseContentSections>()

  if (offer.images.length) {
    sections.add(CruiseContentSections.GALLERY)
  }

  sections.add(CruiseContentSections.OVERVIEW)

  if (offer.itinerary?.length) {
    sections.add(CruiseContentSections.ITINERARY)
  }

  if (offer.ship?.cabinCategories?.length && offer.ship?.decks?.length) {
    sections.add(CruiseContentSections.DECKS_AND_CABINS)
  }

  if (offer.finePrint?.sections.length) {
    sections.add(CruiseContentSections.FINE_PRINT)
  }

  if (offer.ship.shipInfo?.length && offer.cruiseLine.vendorInfo?.length) {
    sections.add(CruiseContentSections.FAQ)
  }

  return sections
}

function CruiseContent({
  cruiseItem,
  offer,
  shareModalConfig,
  currentRegionCode,
  place,
}: Props) {
  const { itinerary, departures, mainDepartureDetails, evergreenInclusions } = offer
  const { departureDate: mainDepartureDate, lowestOverallPriceDetails } = mainDepartureDetails
  const { promotion: promotionDetails } = lowestOverallPriceDetails

  const departuresDatesAnchor = useRef<HTMLDivElement>(null)
  const [hasGotToTabs, setHasGotToTabs] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    const handleScroll = () => {
      const element = departuresDatesAnchor.current
      if (element) {
        const rect = element.getBoundingClientRect()
        setHasGotToTabs(rect.top - 180 <= 0)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const params = useQueryParams()
  const defaultDepartureDate = useMemo(() => {
    // find the earliest departure within the months we've been given for the departures we have
    const months = params.getAll('departureMonths')
    if (months.length > 0) {
      const monthsWithDepartures = months.filter(month => !!departures[month])
      return min(monthsWithDepartures, month => new Date(`${month}-01`).getTime())
    }

    // if we have a main departure date, use that
    if (mainDepartureDate) return mainDepartureDate.split('-').slice(0, 2).join('-')

    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [monthTab, setMonthTab] = useState<string>(defaultDepartureDate ?? Object.keys(departures).sort()[0])
  const [currentSectionTab, setCurrentSectionTab] = useState<CruiseSectionTabValues>(CruiseSectionTabValues.DATES_PRICING)
  const [selectedDepartureYear, setYear] = useState(defaultDepartureDate?.substring(0, 4) ?? Object.keys(departures).sort()[0]?.substring(0, 4))

  useEffect(() => {
    dispatch(fetchPlaceByName(offer.departurePort))
  }, [dispatch, offer])

  const contentSections = useMemo(
    () => determineCruiseContentSections(offer),
    [offer],
  )

  const departureYears = Object.keys(departures).reduce(
    (set, departureDate) => (set.add(departureDate.split('-')[0])),
    new Set<string>(),
  )
  const onHandleSelectDeparture = useCallback((departure: App.CruiseDepartureDetails) => {
    fireInteractionEvent(cruiseSelectDepartureClick(departure.id))

    if (departure.departureDate && departure.arrivalDate) {
      const dates = {
        startDate: departure.departureDate,
        endDate: departure.arrivalDate,
      }

      const item = generateCruiseCheckoutItem(
        offer.id,
        departure.id,
        dates,
        { adults: 0, children: 0, infants: 0 },
      )

      if (cruiseItem) {
        // cruise can only support one cruise item in checkout cart
        dispatch(removeItem(cruiseItem.itemId))
      }
      dispatch(addToCheckoutCart([item]))
      dispatch(setInFlowCheckoutStep(CRUISE_CHECKOUT_INFLOW.TRAVELERS))
      dispatch(pushWithRegion('/checkout/cruises'))
    }
  }, [dispatch, offer, cruiseItem])

  const scrollToPackageOptions = useCallback((tab:string) => {
    if (tab === 'dates-and-pricing') {
      setCurrentSectionTab(CruiseSectionTabValues.DATES_PRICING)
    }
    departuresDatesAnchor.current?.scrollIntoView()
  }, [])

  const hasValidDepartures = useMemo(() => offerHasValidDepartures(offer), [offer])

  const SECTION_TABS: SectionTabs[] = useMemo(() => {
    const tabs: SectionTabs[] = [
      { label: 'Dates & Pricing', value: CruiseSectionTabValues.DATES_PRICING },
      { label: 'Itinerary', value: CruiseSectionTabValues.ITINERARY },
      { label: 'About the ship', value: CruiseSectionTabValues.ABOUT_SHIP },
    ]
    if (contentSections.has(CruiseContentSections.FAQ)) tabs.push({ label: 'Frequently asked questions', value: CruiseSectionTabValues.FAQ })
    return tabs
  }, [contentSections])

  const handleTabsChange = (tab: CruiseSectionTabValues) => setCurrentSectionTab(tab)
  const velocityFeatures = getApplicableVelocityFeatures(lowestOverallPriceDetails.total ?? 0)

  const recommendationFilters = useMemo(() =>
    offer ? generateFilters(
      offer.type,
      offer.departurePort,
      place,
      offer.cruiseLine,
    ) : {}
  , [place, offer])

  useEffect(() => {
    const availableCruiseRegions = config.STANDALONE_CRUISE_REGIONS?.split(',') ?? []
    if (!availableCruiseRegions.includes(currentRegionCode)) {
      dispatch(replaceWithRegion('/'))
    }
  }, [dispatch, currentRegionCode])

  useEffect(() => {
    if (params.getAll('departureMonths').length > 0) {
      // remove params on page landing, it was only for initialisation
      const nextParams = new URLSearchParams(params)
      nextParams.delete('departureMonths')
      dispatch(replace({ search: nextParams.toString() }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <CruiseHeadData offer={offer} />
      <CruiseStickyHeader
        onView={scrollToPackageOptions}
        priceFrom={lowestOverallPriceDetails}
        visible={hasGotToTabs}
        title={offer.name}
        tabs={SECTION_TABS}
        currentSectionTab={currentSectionTab}
        hasValidDepartures={hasValidDepartures}
        handleTabsChange={handleTabsChange}
      />
      <div id="CRUISE_CONTENT_TOP_ANCHOR" aria-hidden="true"></div>

      {!hasValidDepartures && <Pane>
        <CruiseSoldoutCard offer={offer} />
      </Pane>}

      {contentSections.has(CruiseContentSections.GALLERY) && (
        <Pane>
          <GalleryContainer>
            <ImageGallery
              images={offer.images}
              arrowBehaviour="always-visible"
              topLeft={
                <Group direction="vertical" gap={4}>
                  <ProductTypeLabel productType={offer.productType} />
                  <PartnershipLabel canEarn={velocityFeatures.canEarn} canUse={velocityFeatures.canBurn} variant="icon" />
                </Group>
              }
            />
          </GalleryContainer>
        </Pane>
      )}

      <Pane>
        <OverviewSectionContainer>
          <VerticalSpacer gap={16}>
            {!!evergreenInclusions?.length && <Label startIcon={<SolidFireIcon />} kind="primary" variant="highlight-secondary">Special offer</Label>}
            {!!promotionDetails?.isNew && <UrgencyLabel variant="speed">NEW</UrgencyLabel>}
            {!!promotionDetails?.isEndingIn && promotionDetails?.isEndingIn <= 7 && <UrgencyLabel variant="critical2">OFFER ENDS IN {pluralizeToString('day', promotionDetails.isEndingIn)} </UrgencyLabel>}

            <CruiseOverview
              offer={offer}
              showPackageOptions={() => scrollToPackageOptions('dates-and-pricing')}
              shareModalConfig={shareModalConfig}
            />
          </VerticalSpacer>
        </OverviewSectionContainer>
      </Pane>

      <Pane>
        <ScrollAnchor ref={departuresDatesAnchor} />
        <LayoutContainer>
          <SectionTabButtonsGroup fit="auto">
            {SECTION_TABS.map((tab) => (
              <TabButton
                size="medium"
                key={tab.value}
                isActive={tab.value === currentSectionTab}
                onClick={() => handleTabsChange(tab.value)}>{tab.label}</TabButton>
            ))}
          </SectionTabButtonsGroup>
        </LayoutContainer>
      </Pane>

      {currentSectionTab === 'DATES_PRICING' && (
        <CruiseOfferPageDepartures
          offer={offer}
          selectedDepartureYear={selectedDepartureYear}
          departureYears={departureYears}
          departures={departures}
          monthTab={monthTab}
          hasValidDepartures={hasValidDepartures}
          setYear={setYear}
          setMonthTab={setMonthTab}
          onHandleSelectDeparture={onHandleSelectDeparture}
        />
      )}

      {currentSectionTab === 'ITINERARY' && contentSections.has(CruiseContentSections.ITINERARY) && (
        <CruiseOfferPageItinerary itinerary={itinerary} />
      )}

      {currentSectionTab === 'ABOUT_SHIP' && (
        <CruiseOfferPageAboutShip
          offer={offer}
          contentSections={contentSections}
          hasGotToTabs={hasGotToTabs}
          scrollToPackageOptions={scrollToPackageOptions}
        />
      )}

      {currentSectionTab === 'FAQ' && (
        <Pane>
          <SectionContainer size="md">
            <Heading
              align="center"
              variant="heading2"
              className="section-title"
            >{CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.FAQ]}</Heading>

            <div className="section-content" data-testid="faq-section">
              <CruiseFAQ ship={offer.ship} cruiseLine={offer.cruiseLine} />
            </div>
          </SectionContainer>
        </Pane>
      )}

      <PaletteProvider type="inverse">
        <USPPane>
          <LayoutContainer>
            <Heading colour="neutral-one" variant="heading3" align="start">Why you should book with us</Heading>
            <CruiseVerticalUSPs/>
          </LayoutContainer>
        </USPPane>
      </PaletteProvider>

      <CruiseStickyFooter
        onView={scrollToPackageOptions}
        priceFrom={lowestOverallPriceDetails}
        hasValidDepartures={hasValidDepartures}
      />
      {recommendationFilters.departurePlaceId && <ErrorBoundary fallback={null}>
        <CruiseRecommendations offerId={offer.id} filters={recommendationFilters}/>
      </ErrorBoundary>}
    </>
  )
}

function mapStateToProps(appState: App.State) {
  return {
    cruiseItem: getCruiseItems(appState)[0],
    isSpoofed: appState.auth.account.isSpoofed,
    travellers: appState.cruise.travelers,
    departureId: appState.cruise.departureId,
    previousRoute: appState.routeHistory.previous,
    isVelocityEnabled: appState.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
    currentRegionCode: appState.geo.currentRegionCode,
    place: appState.destination.locationToPlace,
  }
}

export default withRouter(connect(mapStateToProps)(CruiseContent))
